import { useState, useEffect } from "react";
import "./CreateCoupon.css";
import { useAdminAuth } from "../../context/AdminAuthContext";
import Swal from "sweetalert2";
import { FaTimes, FaSearch } from "react-icons/fa";

export default function CouponManagement() {
  const [coupons, setCoupons] = useState([]);
  const { products } = useAdminAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    coupon: "",
    couponStatus: "active",
    couponExpiration: "",
    applicableProductsId: [],
    isMaxUsages: false,
    maxUsagesCount: 0,
    isMaxUsagesPerUser: false,
    maxUsagesPerUserCount: 0,
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem("admintoken");
      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/all/copun-codes",
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch coupons");
      const data = await response.json();
      setCoupons(data.coupons);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch coupons",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      coupon: "",
      couponStatus: "active",
      couponExpiration: "",
      applicableProductsId: [],
      isMaxUsages: false,
      maxUsagesCount: 0,
      isMaxUsagesPerUser: false,
      maxUsagesPerUserCount: 0,
    });
  };

  const handleCreateCoupon = async () => {
    // Validate form
    if (!formData.coupon.trim()) {
      Swal.fire({
        icon: "warning",
        text: "Coupon code is required",
      });
      return;
    }

    if (!formData.couponExpiration) {
      Swal.fire({
        icon: "warning",
        text: "Expiration date is required",
      });
      return;
    }

    if (formData.applicableProductsId.length === 0) {
      Swal.fire({
        icon: "warning",
        text: "Please select at least one product",
      });
      return;
    }

    try {
      setLoading(true);
      const adminToken = localStorage.getItem("admintoken");
      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/copun-code",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        Swal.fire({
          icon: "warning",
          text: data.message || "Failed to create coupon",
        });
        return;
      }
      
      Swal.fire({
        icon: "success",
        text: "Coupon created successfully",
      });
      
      setModalOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error("Error creating coupon:", error);
      Swal.fire({
        icon: "error",
        text: "An error occurred while creating the coupon",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (id) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this coupon?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (confirmation.isConfirmed) {
      try {
        setLoading(true);
        const adminToken = localStorage.getItem("admintoken");
        const response = await fetch(
          `https://admin.riymay.com/api/v1/admin/copun-code/${id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${adminToken}` },
          }
        );
        if (!response.ok) throw new Error("Failed to delete coupon");
        Swal.fire({
          icon: "success",
          text: "Coupon deleted successfully",
        });
        fetchCoupons();
      } catch (error) {
        console.error("Error deleting coupon:", error);
        Swal.fire({
          icon: "error",
          text: "Failed to delete coupon",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditCoupon = (coupon) => {
    setFormData({
      coupon: coupon.coupon,
      couponStatus: coupon.couponStatus,
      couponExpiration: new Date(coupon.couponExpiration).toISOString().split('T')[0],
      applicableProductsId: coupon.applicableProductsId || [],
      isMaxUsages: !!coupon.maxUsagesCount,
      maxUsagesCount: coupon.maxUsagesCount || 0,
      isMaxUsagesPerUser: !!coupon.maxUsagesPerUserCount,
      maxUsagesPerUserCount: coupon.maxUsagesPerUserCount || 0,
      _id: coupon._id,
    });
    setModalOpen(true);
  };

  const handleUpdateCoupon = async () => {
    if (!formData._id) {
      handleCreateCoupon();
      return;
    }

    try {
      setLoading(true);
      const adminToken = localStorage.getItem("admintoken");
      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/copun-code",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify(formData),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update coupon");
      }
      
      Swal.fire({
        icon: "success",
        text: "Coupon updated successfully",
      });
      
      setModalOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error("Error updating coupon:", error);
      Swal.fire({
        icon: "error",
        text: error.message || "Failed to update coupon",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelection = (id) => {
    setFormData((prev) => ({
      ...prev,
      applicableProductsId: prev.applicableProductsId.includes(id)
        ? prev.applicableProductsId.filter((pid) => pid !== id)
        : [...prev.applicableProductsId, id],
    }));
  };

  const handleSelectAllProducts = () => {
    if (formData.applicableProductsId.length === products.length) {
      // If all are selected, deselect all
      setFormData((prev) => ({
        ...prev,
        applicableProductsId: [],
      }));
    } else {
      // Otherwise select all
      setFormData((prev) => ({
        ...prev,
        applicableProductsId: products.map(product => product._id),
      }));
    }
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="coupon-container">
      <div className="coupon-header">
        <h1>Coupon Management</h1>
        <button
          className="create-coupon-btn"
          onClick={() => {
            resetForm();
            setModalOpen(true);
          }}
        >
          Create Coupon +
        </button>
      </div>

      {loading && <div className="coupon-loading">Loading...</div>}

      {modalOpen && (
        <div className="coupon-modal-overlay">
          <div className="coupon-modal">
            <div className="coupon-modal-header">
              <h2>{formData._id ? "Update Coupon" : "Create Coupon"}</h2>
              <button 
                className="coupon-modal-close" 
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="coupon-form">
              <div className="form-group">
                <label>Coupon Code</label>
                <input
                  className="coupon-input"
                  placeholder="Enter coupon code"
                  value={formData.coupon}
                  onChange={(e) =>
                    setFormData({ ...formData, coupon: e.target.value.toUpperCase() })
                  }
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Expiration Date</label>
                <input
                  className="coupon-input"
                  type="date"
                  value={formData.couponExpiration}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) =>
                    setFormData({ ...formData, couponExpiration: e.target.value })
                  }
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  className="coupon-input"
                  value={formData.couponStatus}
                  onChange={(e) =>
                    setFormData({ ...formData, couponStatus: e.target.value })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="form-group">
                <div className="product-selection-header">
                  <label>Select Products</label>
                  <div className="product-selection-actions">
                    <button 
                      type="button" 
                      className="select-all-btn"
                      onClick={handleSelectAllProducts}
                    >
                      {formData.applicableProductsId.length === products.length 
                        ? "Deselect All" 
                        : "Select All"}
                    </button>
                    <div className="product-search">
                      {/* <FaSearch className="search-icon" /> */}
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="product-list-container">
                  <div className="product-list">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <div
                          key={product._id}
                          className={`product-tag ${
                            formData.applicableProductsId.includes(product._id)
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => handleProductSelection(product._id)}
                        >
                          {product.name}
                        </div>
                      ))
                    ) : (
                      <div className="no-products">No products found</div>
                    )}
                  </div>
                </div>
                
                <div className="selected-count">
                  {formData.applicableProductsId.length} products selected
                </div>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isMaxUsages}
                    onChange={(e) =>
                      setFormData({ ...formData, isMaxUsages: e.target.checked })
                    }
                  />
                  <span>Limit Total Usages</span>
                </label>
                
                {formData.isMaxUsages && (
                  <input
                    className="coupon-input"
                    type="number"
                    min="1"
                    value={formData.maxUsagesCount}
                    onChange={(e) =>
                      setFormData({ ...formData, maxUsagesCount: parseInt(e.target.value) || 0 })
                    }
                    placeholder="Max total usages"
                  />
                )}
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isMaxUsagesPerUser}
                    onChange={(e) =>
                      setFormData({ ...formData, isMaxUsagesPerUser: e.target.checked })
                    }
                  />
                  <span>Limit Usages Per User</span>
                </label>
                
                {formData.isMaxUsagesPerUser && (
                  <input
                    className="coupon-input"
                    type="number"
                    min="1"
                    value={formData.maxUsagesPerUserCount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxUsagesPerUserCount: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="Max usages per user"
                  />
                )}
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={formData._id ? handleUpdateCoupon : handleCreateCoupon}
                  disabled={loading}
                >
                  {loading ? "Processing..." : formData._id ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="coupon-grid">
        {coupons.length > 0 ? (
          coupons.map((coupon) => (
            <div key={coupon._id} className="coupon-card">
              <div className="coupon-code">{coupon.coupon}</div>
              <div className="coupon-details">
                <div className={`coupon-status ${coupon.couponStatus}`}>
                  {coupon.couponStatus}
                </div>
                <div className="coupon-expiry">
                  Expires: {formatDate(coupon.couponExpiration)}
                </div>
                {coupon.maxUsagesCount && (
                  <div className="coupon-usage">
                    Usage: {coupon.usageCount || 0}/{coupon.maxUsagesCount}
                  </div>
                )}
                {coupon.maxUsagesPerUserCount && (
                  <div className="coupon-usage-per-user">
                    Per User: {coupon.maxUsagesPerUserCount}
                  </div>
                )}
                <div className="coupon-products">
                  Products: {coupon.applicableProductsId?.length || 0}
                </div>
              </div>
              <div className="coupon-actions">
                <button
                  className="edit-btn"
                  onClick={() => handleEditCoupon(coupon)}
                >
                  Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteCoupon(coupon._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-coupons">No coupons found</div>
        )}
      </div>
    </div>
  );
}